import { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { Link } from "react-router-dom";

import { auth } from "../services/firebase";
import { subscribeToAdminData } from "../services/admin";
import { updateOrderStatus } from "../services/firestore";

import { isAdminEmail, nextDeliveryDate, toDateKey } from "../utils/admin";

import { products } from "../data";
import "../css/Admin.css";

import {
  AdminTabs,
  OrderTable,
  ScheduleTable,
  StatCard,
  UserTable,
} from "../components/admin";

const empty = {
  users: [],
  addresses: [],
  schedules: [],
  orders: [],
};

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [orderFrequencyTab, setOrderFrequencyTab] = useState("one-time");
  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const [currentUser, setCurrentUser] = useState(
    () => auth?.currentUser || null,
  );

  const [authLoading, setAuthLoading] = useState(Boolean(auth));

  /*
   * ---------------------------------------------------------
   * ADMIN ACCESS
   * ---------------------------------------------------------
   */

  const allowed = isAdminEmail(currentUser?.email);

  /*
   * ---------------------------------------------------------
   * AUTH LISTENER
   * ---------------------------------------------------------
   */

  useEffect(() => {
    if (!auth) {
      setAuthLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthLoading(false);
    });

    return unsubscribe;
  }, []);

  /*
   * ---------------------------------------------------------
   * REALTIME ADMIN DATA
   * ---------------------------------------------------------
   */

  useEffect(() => {
    if (authLoading) {
      return undefined;
    }

    if (!allowed) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError("");

    const stop = subscribeToAdminData(
      (next) => {
        setData(next);
        setLastUpdated(new Date());
        setLoading(false);
      },
      (err) => {
        setError(
          err?.message ||
            "Unable to load realtime admin data. Check Firestore rules.",
        );

        setLoading(false);
      },
    );

    return () => stop?.();
  }, [allowed, authLoading]);

  /*
   * ---------------------------------------------------------
   * DERIVED DATA
   *
   * IMPORTANT:
   * All hooks are declared BEFORE any conditional return.
   * This prevents:
   *
   * "Rendered more hooks than during the previous render."
   * ---------------------------------------------------------
   */

  const usersById = useMemo(
    () => Object.fromEntries(data.users.map((user) => [user.id, user])),
    [data.users],
  );

  const addressesById = useMemo(
    () =>
      Object.fromEntries(
        data.addresses.map((address) => [address.id, address]),
      ),
    [data.addresses],
  );

  const schedulesById = useMemo(
    () => Object.fromEntries(data.schedules.map((schedule) => [schedule.id, schedule])),
    [data.schedules],
  );

  const today = toDateKey();

  const tomorrow = toDateKey(new Date(Date.now() + 86400000));

  const activeSchedules = useMemo(
    () =>
      data.schedules.filter((schedule) =>
        ["active", "Active"].includes(schedule.status),
      ),
    [data.schedules],
  );

  const scheduleRows = useMemo(
    () =>
      activeSchedules
        .map((schedule) => ({
          schedule,
          date: nextDeliveryDate(schedule),
        }))
        .filter((item) => item.date),
    [activeSchedules],
  );

  const todayOrders = useMemo(
    () => data.orders.filter((order) => order.deliveryDate === today),
    [data.orders, today],
  );

  const tomorrowOrders = useMemo(
    () => data.orders.filter((order) => order.deliveryDate === tomorrow),
    [data.orders, tomorrow],
  );

  /*
   * Recurring schedules which fall on today but do not
   * yet have an individual order document.
   */

  const virtualToday = useMemo(
    () =>
      scheduleRows.filter(
        ({ schedule, date }) =>
          date === today &&
          !data.orders.some(
            (order) =>
              order.scheduleId === schedule.id && order.deliveryDate === today,
          ),
      ),
    [scheduleRows, data.orders, today],
  );

  /*
   * Recurring schedules which fall on tomorrow but do not
   * yet have an individual order document.
   */

  const virtualTomorrow = useMemo(
    () =>
      scheduleRows.filter(
        ({ schedule, date }) =>
          date === tomorrow &&
          !data.orders.some(
            (order) =>
              order.scheduleId === schedule.id &&
              order.deliveryDate === tomorrow,
          ),
      ),
    [scheduleRows, data.orders, tomorrow],
  );

  /*
   * Search/filter orders
   */

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) {
      return data.orders;
    }

    return data.orders.filter((order) => {
      const user = usersById[order.userId];

      return [
        user?.name,
        user?.email,
        user?.mobile,
        order.productName,
        order.productId,
        order.status,
        order.deliveryDate,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [data.orders, search, usersById]);

  // Frequency-specific order tabs. Resolve the frequency from the order first,
  // then its linked schedule for older order documents.
  const frequencyOrders = useMemo(() => {
    const normalizeFrequency = (value) => {
      const frequency = String(value || "").trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\\s+/g, " ");
      if (!frequency || ["one time", "onetime", "one-time", "once"].includes(frequency)) return "one-time";
      if (["daily", "every day", "everyday"].includes(frequency)) return "daily";
      if (["3 days a week", "three days a week", "3 day a week", "three day a week", "3 days/week", "three days/week"].includes(frequency)) return "three-days";
      if (["weekly", "every week", "once a week"].includes(frequency)) return "weekly";
      if (["monthly", "every month", "once a month"].includes(frequency)) return "monthly";
      return frequency;
    };

    return filteredOrders.filter((order) => {
      const schedule = order.scheduleId ? schedulesById[order.scheduleId] : null;
      const rawFrequency = order.frequency || schedule?.frequency ||
        (order.recurring ? "Recurring" : "One Time");
      const key = normalizeFrequency(rawFrequency);
      // Some legacy orders store recurring only as a boolean and have no frequency.
      const effectiveKey = key === "recurring" && schedule?.frequency
        ? normalizeFrequency(schedule.frequency)
        : key;
      return effectiveKey === orderFrequencyTab;
    });
  }, [filteredOrders, schedulesById, orderFrequencyTab]);

  /*
   * ---------------------------------------------------------
   * ORDER STATUS UPDATE
   * ---------------------------------------------------------
   */

  const changeStatus = async (id, status) => {
    try {
      await updateOrderStatus(id, status);

      /*
       * Optimistic local update.
       *
       * Firestore realtime listener will also update the data
       * when the server confirms the change.
       */

      setData((previous) => ({
        ...previous,
        orders: previous.orders.map((order) =>
          order.id === id
            ? {
                ...order,
                status,
              }
            : order,
        ),
      }));
    } catch (err) {
      setError(err?.message || "Unable to update order status.");
    }
  };

  /*
   * ---------------------------------------------------------
   * LOADING AUTH
   * ---------------------------------------------------------
   */

  if (authLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <i className="fa-solid fa-spinner fa-spin" /> Checking admin access…
        </div>
      </div>
    );
  }

  /*
   * ---------------------------------------------------------
   * ADMIN ACCESS DENIED
   * ---------------------------------------------------------
   */

  if (!allowed) {
    return (
      <div className="admin-page">
        <div className="admin-login-card">
          <span className="admin-lock">
            <i className="fa-solid fa-shield-halved" />
          </span>

          <span className="eyebrow">FreshLoop operations</span>

          <h1>Admin access only</h1>

          <p>
            Sign in with your configured admin email. The dashboard is
            intentionally separate from the customer experience.
          </p>

          <Link to="/login?redirect=%2Fadmin" className="btn btn-brand w-100">
            Go to admin login
          </Link>

          <Link to="/home" className="text-link justify-content-center mt-3">
            Back to FreshLoop
          </Link>
        </div>
      </div>
    );
  }

  /*
   * ---------------------------------------------------------
   * UPCOMING SCHEDULE ROWS
   * ---------------------------------------------------------
   */

  const renderScheduleRows = (rows) => (
    <div className="admin-upcoming-list">
      {rows.map(({ schedule, date }) => {
        const user = usersById[schedule.userId];

        const product = products.find(
          (productItem) => productItem.id === schedule.productId,
        );

        return (
          <article
            key={`${schedule.id}-${date}`}
            className="admin-upcoming-row"
          >
            <span className="admin-upcoming-icon">
              <i
                className={`fa-solid ${
                  schedule.type === "fruit_basket"
                    ? "fa-basket-shopping"
                    : "fa-glass-water"
                }`}
              />
            </span>

            <div>
              <strong>{user?.name || "Unknown user"}</strong>

              <span>
                {product?.name || schedule.productId} · {schedule.frequency || "Recurring"} · {schedule.deliveryTimeSlot || "Time not set"}
              </span>
              <small className="admin-upcoming-days">
                {schedule.days?.length ? schedule.days.join(", ") : "One-time delivery"}
              </small>
            </div>

            <b>
              {date === today ? "Today" : date === tomorrow ? "Tomorrow" : date}
            </b>
          </article>
        );
      })}
    </div>
  );

  /*
   * ---------------------------------------------------------
   * OPTIONAL HELPER
   * ---------------------------------------------------------
   */

  const combinedDaySchedules = (day) =>
    scheduleRows.filter((item) => item.date === day);

  /*
   * ---------------------------------------------------------
   * ADMIN DASHBOARD
   * ---------------------------------------------------------
   */

  return (
    <div className="admin-page">
      {/* ================= HEADER ================= */}

      <header className="admin-header">
        <div>
          <Link to="/home" className="admin-back">
            <i className="fa-solid fa-arrow-left" /> Customer app
          </Link>

          <span className="eyebrow">FreshLoop operations</span>

          <h1>Delivery control center</h1>

          <p>
            See every customer, recurring schedule and delivery in one place.
          </p>
        </div>

        <div className="admin-header-actions">
          <span className="admin-user">
            <i className="fa-solid fa-user-shield" /> {currentUser?.email}
          </span>

          <button
            type="button"
            className="btn btn-soft"
            onClick={() => window.location.reload()}
            disabled={loading}
          >
            <i className={`fa-solid fa-rotate ${loading ? "fa-spin" : ""}`} />{" "}
            Refresh
          </button>
        </div>
      </header>

      {/* ================= CONTENT ================= */}

      <main className="container-xl admin-content">
        <AdminTabs active={tab} onChange={setTab} />

        {/* ================= ERROR ================= */}

        {error && (
          <div className="alert alert-danger d-flex justify-content-between align-items-center">
            <span>{error}</span>

            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}

        {/* ================= STATS ================= */}

        <section className="admin-stats">
          <StatCard
            icon="fa-sun"
            label="Today"
            value={todayOrders.length + virtualToday.length}
            sub="deliveries"
          />

          <StatCard
            icon="fa-calendar-day"
            label="Tomorrow"
            value={tomorrowOrders.length + virtualTomorrow.length}
            sub="deliveries"
            tone="orange"
          />

          <StatCard
            icon="fa-repeat"
            label="Active schedules"
            value={activeSchedules.length}
            sub="recurring customers"
            tone="yellow"
          />

          <StatCard
            icon="fa-users"
            label="Customers"
            value={data.users.length}
            sub="registered users"
            tone="blue"
          />
        </section>

        {/* ================= TOOLBAR ================= */}

        <div className="admin-toolbar">
          <div className="admin-search">
            <i className="fa-solid fa-magnifying-glass" />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search customer, phone, product or status"
            />
          </div>

          <small>
            {lastUpdated
              ? `Updated ${lastUpdated.toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`
              : "Loading…"}
          </small>
        </div>

        {/* ================= LOADING ================= */}

        {loading && !data.users.length && (
          <div className="admin-loading">
            <i className="fa-solid fa-spinner fa-spin" /> Loading Firebase data…
          </div>
        )}

        {/* =================================================
            OVERVIEW
        ================================================= */}

        {!loading && tab === "overview" && (
          <div className="admin-grid-2">
            {/* TODAY */}

            <section className="admin-panel">
              <div className="admin-panel-head">
                <div>
                  <span className="eyebrow">Operations</span>

                  <h2>Today’s deliveries</h2>
                </div>

                <button
                  type="button"
                  className="text-link btn-reset"
                  onClick={() => setTab("today")}
                >
                  View all <i className="fa-solid fa-arrow-right" />
                </button>
              </div>

              <OrderTable
                orders={todayOrders}
                usersById={usersById}
                addressesById={addressesById}
                schedulesById={schedulesById}
                onStatusChange={changeStatus}
                empty="No saved orders for today."
              />

              {virtualToday.length > 0 && (
                <div className="admin-note">
                  <i className="fa-solid fa-repeat" />

                  <div>
                    <strong>
                      {virtualToday.length} recurring schedule(s) also fall
                      today.
                    </strong>

                    <span>
                      These are calculated from active schedules because no
                      individual order document exists for that date yet.
                    </span>
                  </div>
                </div>
              )}

              {renderScheduleRows(virtualToday)}
            </section>

            {/* TOMORROW */}

            <section className="admin-panel">
              <div className="admin-panel-head">
                <div>
                  <span className="eyebrow">Next shift</span>

                  <h2>Tomorrow</h2>
                </div>

                <button
                  type="button"
                  className="text-link btn-reset"
                  onClick={() => setTab("tomorrow")}
                >
                  View all <i className="fa-solid fa-arrow-right" />
                </button>
              </div>

              <OrderTable
                orders={tomorrowOrders}
                usersById={usersById}
                addressesById={addressesById}
                schedulesById={schedulesById}
                onStatusChange={changeStatus}
                empty="No saved orders for tomorrow."
              />

              {virtualTomorrow.length > 0 && (
                <div className="admin-note">
                  <i className="fa-solid fa-repeat" />

                  <div>
                    <strong>
                      {virtualTomorrow.length} recurring schedule(s) are due
                      tomorrow.
                    </strong>

                    <span>
                      Use the scheduled list to plan procurement and
                      preparation.
                    </span>
                  </div>
                </div>
              )}

              {renderScheduleRows(virtualTomorrow)}
            </section>

            {/* USERS */}

            <section className="admin-panel admin-panel-wide">
              <div className="admin-panel-head">
                <div>
                  <span className="eyebrow">Customers</span>

                  <h2>Recent users</h2>
                </div>

                <button
                  type="button"
                  className="text-link btn-reset"
                  onClick={() => setTab("users")}
                >
                  All users <i className="fa-solid fa-arrow-right" />
                </button>
              </div>

              <UserTable
                users={data.users.slice(0, 5)}
                schedules={data.schedules}
                orders={data.orders}
                addresses={data.addresses}
              />
            </section>
          </div>
        )}

        {/* =================================================
            TODAY
        ================================================= */}

        {!loading && tab === "today" && (
          <section className="admin-panel">
            <div className="admin-panel-head">
              <div>
                <span className="eyebrow">Operations</span>

                <h2>Today’s deliveries</h2>

                <p>
                  Confirmed order instances plus recurring schedules due today.
                </p>
              </div>
            </div>

            <OrderTable
              orders={todayOrders}
              usersById={usersById}
              addressesById={addressesById}
              schedulesById={schedulesById}
              onStatusChange={changeStatus}
              empty="No confirmed order instances for today."
            />

            {virtualToday.length > 0 && (
              <div className="admin-note">
                <i className="fa-solid fa-repeat" />

                <div>
                  <strong>Recurring schedules due today</strong>

                  <span>
                    These are calculated from schedules and have not yet been
                    materialized as order documents.
                  </span>
                </div>
              </div>
            )}

            {renderScheduleRows(virtualToday)}
          </section>
        )}

        {/* =================================================
            TOMORROW
        ================================================= */}

        {!loading && tab === "tomorrow" && (
          <section className="admin-panel">
            <div className="admin-panel-head">
              <div>
                <span className="eyebrow">Preparation</span>

                <h2>Tomorrow’s deliveries</h2>

                <p>Plan fresh ingredients, preparation and delivery routes.</p>
              </div>
            </div>

            <OrderTable
              orders={tomorrowOrders}
              usersById={usersById}
              addressesById={addressesById}
              schedulesById={schedulesById}
              onStatusChange={changeStatus}
              empty="No confirmed order instances for tomorrow."
            />

            {virtualTomorrow.length > 0 && (
              <div className="admin-note">
                <i className="fa-solid fa-repeat" />

                <div>
                  <strong>Recurring schedules due tomorrow</strong>

                  <span>These are calculated from active schedules.</span>
                </div>
              </div>
            )}

            {renderScheduleRows(virtualTomorrow)}
          </section>
        )}

        {/* =================================================
            SCHEDULED
        ================================================= */}

        {!loading && tab === "scheduled" && (
          <section className="admin-panel">
            <div className="admin-panel-head">
              <div>
                <span className="eyebrow">Recurring</span>

                <h2>Active schedules</h2>

                <p>
                  Every customer’s recurring fruit or nutrition plan, including
                  the next delivery date.
                </p>
              </div>
            </div>

            <ScheduleTable
              schedules={activeSchedules}
              usersById={usersById}
              addressesById={addressesById}
            />
          </section>
        )}

        {/* =================================================
            ORDERS
        ================================================= */}

        {!loading && tab === "orders" && (
          <section className="admin-panel">
            <div className="admin-panel-head">
              <div>
                <span className="eyebrow">Delivery history</span>

                <h2>All order instances</h2>

                <p>
                  Each document here represents a confirmed delivery/order
                  instance.
                </p>
              </div>
            </div>

            <div className="admin-order-frequency-tabs" role="tablist" aria-label="Filter orders by schedule">
              {[
                ["one-time", "One time"],
                ["daily", "Daily"],
                ["three-days", "3 days a week"],
                ["weekly", "Weekly"],
                ["monthly", "Monthly"],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={orderFrequencyTab === id}
                  className={orderFrequencyTab === id ? "active" : ""}
                  onClick={() => setOrderFrequencyTab(id)}
                >
                  {label}
                  <span>{filteredOrders.filter((order) => {
                    const schedule = order.scheduleId ? schedulesById[order.scheduleId] : null;
                    const value = String(order.frequency || schedule?.frequency || (order.recurring ? "Recurring" : "One Time")).trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\\s+/g, " ");
                    const normalized = value === "one time" || value === "onetime" || value === "once" ? "one-time"
                      : value === "daily" || value === "every day" || value === "everyday" ? "daily"
                      : ["3 days a week", "three days a week", "3 day a week", "three day a week", "3 days/week", "three days/week"].includes(value) ? "three-days"
                      : ["weekly", "every week", "once a week"].includes(value) ? "weekly"
                      : ["monthly", "every month", "once a month"].includes(value) ? "monthly"
                      : value === "recurring" && schedule?.frequency ? String(schedule.frequency).toLowerCase() : value;
                    return normalized === id;
                  }).length}</span>
                </button>
              ))}
            </div>
            <OrderTable
              orders={frequencyOrders}
              usersById={usersById}
              addressesById={addressesById}
              schedulesById={schedulesById}
              onStatusChange={changeStatus}
              empty={`No ${({
                "one-time": "one-time",
                daily: "daily",
                "three-days": "3-days-a-week",
                weekly: "weekly",
                monthly: "monthly",
              })[orderFrequencyTab]} orders found.`}
            />
          </section>
        )}

        {/* =================================================
            USERS
        ================================================= */}

        {!loading && tab === "users" && (
          <section className="admin-panel">
            <div className="admin-panel-head">
              <div>
                <span className="eyebrow">Customers</span>

                <h2>All users</h2>

                <p>
                  Customer contact information and their schedule/order counts.
                </p>
              </div>
            </div>

            <UserTable
              users={data.users}
              schedules={data.schedules}
              orders={data.orders}
              addresses={data.addresses}
            />
          </section>
        )}
      </main>
    </div>
  );
}
